import { type JsonMessage, Listeners, type Room, type Transport, randomId } from '../transport';

type Wire =
  | { k: 'hello'; from: string; room: string; reply: boolean }
  | { k: 'bye'; from: string; room: string }
  | { k: 'msg'; from: string; room: string; to: string[] | null; msg: JsonMessage }
  | { k: 'bin'; from: string; room: string; to: string[] | null; bytes: Uint8Array };

/**
 * Same-browser transport over BroadcastChannel: lets several tabs of one
 * browser profile play together without any network (dev and offline demos).
 */
class BroadcastRoom implements Room {
  readonly msgs = new Listeners<[string, JsonMessage]>();
  readonly bins = new Listeners<[string, Uint8Array]>();
  readonly joins = new Listeners<[string]>();
  readonly leaves = new Listeners<[string]>();
  readonly known = new Set<string>();
  private lastSeen = new Map<string, number>();
  private heartbeat: ReturnType<typeof setInterval>;

  constructor(
    readonly id: string,
    readonly selfId: string,
    private bc: BroadcastChannel,
  ) {
    this.heartbeat = setInterval(() => {
      this.post({ k: 'hello', from: this.selfId, room: this.id, reply: false });
      const now = Date.now();
      for (const [p, t] of this.lastSeen) {
        if (now - t > 6000) {
          this.lastSeen.delete(p);
          if (this.known.delete(p)) this.leaves.emit(p);
        }
      }
    }, 2000);
  }

  post(w: Wire): void {
    this.bc.postMessage(w);
  }

  handle(w: Wire): void {
    if (w.room !== this.id || w.from === this.selfId) return;
    this.lastSeen.set(w.from, Date.now());
    if (w.k === 'hello') {
      if (!this.known.has(w.from)) {
        this.known.add(w.from);
        this.joins.emit(w.from);
        this.post({ k: 'hello', from: this.selfId, room: this.id, reply: true });
      }
      return;
    }
    if (w.k === 'bye') {
      if (this.known.delete(w.from)) this.leaves.emit(w.from);
      return;
    }
    if (w.to && !w.to.includes(this.selfId)) return;
    if (!this.known.has(w.from)) {
      this.known.add(w.from);
      this.joins.emit(w.from);
    }
    if (w.k === 'msg') this.msgs.emit(w.from, w.msg);
    else this.bins.emit(w.from, w.bytes);
  }

  peers(): string[] {
    return [...this.known];
  }

  private to(t?: string | readonly string[]): string[] | null {
    return t === undefined ? null : typeof t === 'string' ? [t] : [...t];
  }

  send(msg: JsonMessage, to?: string | readonly string[]): void {
    this.post({ k: 'msg', from: this.selfId, room: this.id, to: this.to(to), msg });
  }

  sendBinary(bytes: Uint8Array, to?: string | readonly string[]): void {
    this.post({ k: 'bin', from: this.selfId, room: this.id, to: this.to(to), bytes });
  }

  onMessage(cb: (from: string, msg: JsonMessage) => void): () => void {
    return this.msgs.add(cb);
  }
  onBinary(cb: (from: string, bytes: Uint8Array) => void): () => void {
    return this.bins.add(cb);
  }
  onPeerJoin(cb: (id: string) => void): () => void {
    return this.joins.add(cb);
  }
  onPeerLeave(cb: (id: string) => void): () => void {
    return this.leaves.add(cb);
  }

  async leave(): Promise<void> {
    clearInterval(this.heartbeat);
    this.post({ k: 'bye', from: this.selfId, room: this.id });
    this.known.clear();
    await Promise.resolve();
  }
}

export class BroadcastTransport implements Transport {
  readonly kind = 'broadcast' as const;
  readonly selfId = randomId(10);
  private bc: BroadcastChannel;
  private rooms = new Set<BroadcastRoom>();

  constructor(channel = 'aetherfall-online-net') {
    this.bc = new BroadcastChannel(channel);
    this.bc.onmessage = (e: MessageEvent<Wire>) => {
      for (const r of this.rooms) r.handle(e.data);
    };
  }

  init(): Promise<void> {
    return Promise.resolve();
  }

  async join(roomId: string): Promise<Room> {
    const room = new BroadcastRoom(roomId, this.selfId, this.bc);
    this.rooms.add(room);
    room.post({ k: 'hello', from: this.selfId, room: roomId, reply: false });
    const origLeave = room.leave.bind(room);
    room.leave = async () => {
      this.rooms.delete(room);
      await origLeave();
    };
    await Promise.resolve();
    return room;
  }
}
